import { useMemo } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui'
import { formatDate } from '../../lib/transaction-utils'

interface PageInfoLike {
  hasNextPage?: boolean
  hasPreviousPage?: boolean
  startCursor?: string | null
  endCursor?: string | null
}

export interface AuditLogNode {
  id: string
  createdAt: string
  user?: string | null
  action: string
  entityId?: string | null
  companyName?: string | null
  newData?: string | null
  ipAddress?: string | null
}

export interface AuditLogsTableProps {
  logs: AuditLogNode[]
  totalCount?: number
  pageSize: number
  pageInfo?: PageInfoLike
  // pagination UI behavior
  onNextPage: () => void
  onPreviousPage: () => void
  onPageSizeChange: (newSize: number) => void
}

function CompanyCell({ log }: { log: AuditLogNode }) {
  if (!log.companyName) return <span className="text-muted-foreground">-</span>

  let companyId: string | null = null
  if (log.newData) {
    try {
      const parsed = JSON.parse(log.newData)
      if (typeof parsed?.companyId === 'string') companyId = parsed.companyId
    } catch {
      // ignore
    }
  }

  const shortened =
    log.companyName.length > 12 ? `${log.companyName.slice(0, 12)}...` : log.companyName

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default">{shortened}</span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{log.companyName}</p>
          {companyId && <p className="text-xs opacity-70">{companyId}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function AuditLogsTable({
  logs,
  totalCount,
  pageSize,
  pageInfo,
  onNextPage,
  onPreviousPage,
  onPageSizeChange,
}: AuditLogsTableProps) {
  const handlePageSizeChange = (value: string) => {
    const parsed = Number(value)
    if (!Number.isNaN(parsed) && parsed > 0) onPageSizeChange(parsed)
  }

  const getActionColor = useMemo(() => {
    return (action: string) => {
      const actionLower = action.toLowerCase()
      if (actionLower.includes('create')) return 'bg-green-100 text-green-700'
      if (actionLower.includes('update') || actionLower.includes('assign'))
        return 'bg-blue-100 text-blue-700'
      if (actionLower.includes('delete') || actionLower.includes('remove'))
        return 'bg-red-100 text-red-700'
      if (actionLower.includes('login') || actionLower.includes('logout'))
        return 'bg-purple-100 text-purple-700'
      return 'bg-gray-100 text-gray-700'
    }
  }, [])

  return (
    <div className="space-y-4">
      <div className="rounded-md">
        <Table>
          <TableHeader>
            <TableRow className="h-[60px]">
              <TableHead>Date</TableHead>
              <TableHead className="w-[100px]">ID</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Entity ID</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>IP Address</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  No audit logs found
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id} className="h-[60px]">
                  <TableCell className="text-sm">{formatDate(log.createdAt)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {log.id.slice(0, 8)}...
                  </TableCell>
                  <TableCell className="font-medium">
                    {log.user || <span className="text-muted-foreground">System</span>}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className={getActionColor(log.action)}>
                      {log.action}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {log.entityId ? (
                      <>{log.entityId.slice(0, 8)}...</>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    <CompanyCell log={log} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {log.ipAddress || <span className="text-muted-foreground">-</span>}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <div className="flex items-center justify-between px-4 py-3 border-t">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Items per page:</span>
              <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="text-sm text-muted-foreground">
              {typeof totalCount === 'number'
                ? `Showing ${logs.length} of ${totalCount} entries`
                : `Showing ${logs.length} entries`}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onPreviousPage}
              disabled={!pageInfo?.hasPreviousPage}
              className="flex items-center gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={onNextPage}
              disabled={!pageInfo?.hasNextPage}
              className="flex items-center gap-1"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
